// The line drawer of the Budgets tab (SPEC §8.6): one line or unbudgeted row of a vessel's sheet,
// and its ledger for the period: base budget, transfers and additions, and the POs, with a running balance.
import { useQuery } from "@tanstack/react-query";
import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react";
import { budgetLedger, budgetMoves, formatDate, sameRow, UNBUDGETED_LABEL, type BudgetRowRef, type ForecastNote, type LedgerEntry } from "@po/shared";
import { api, qs, type BudgetLine } from "../lib/api";
import { useDashboard } from "../pages/Dashboard";
import { ChangeMark, changeKey, indexChanges, OverChip, signed, usd, UsedBar } from "./budget";
import type { BudgetLineStep, DrawerRef } from "./Drawer";

const periodLabel = (p: { year: number; quarter: number | null }) => (p.quarter === null ? `Year ${p.year}` : `Q${p.quarter} ${p.year}`);

const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** The ledger's Type column, as in a budget ledger: transfers, additions, deductions, POs. */
const TYPE: Record<ForecastNote["kind"], string> = { shift: "TRANSFER", additional: "ADDITIONAL", deduction: "DEDUCTION", adjustment: "ADJUSTMENT" };

const left = { textAlign: "left" } as const;
/** Anything that adds to the line's balance (a budget move, or a PO giving budget back, e.g. a rejection) reads green. */
export const ADDED = { color: "var(--budget-added)", fontWeight: 600 } as const;
const adds = (e: LedgerEntry) => e.kind !== "base" && e.amountUsd >= 0.005;
/** A budget move that takes from the line (a shift out, a deduction) reads red. */
const TAKEN = { color: "var(--budget-over)", fontWeight: 600 } as const;
const takes = (e: LedgerEntry) => e.kind === "note" && e.amountUsd <= -0.005;

/**
 * The line's movements in the period as a ledger (SPEC §8.6): base budget, Sertica's transfers and
 * additions, each PO's spend, and each PO under approval's reserve (other cost), by date, with the
 * running balance; red once it is below zero.
 */
function Ledger({ l, onOpen, reserveOnly }: { l: BudgetLine; onOpen: (r: DrawerRef) => void; reserveOnly: boolean }) {
  const r = l.row;
  // With only the POs under approval, a running balance would mislead: no Balance column then.
  const budgetUsd = r.kind === "line" && !reserveOnly ? r.budgetUsd : null;
  const { year, quarter } = l.period;
  const start = `${year}-${String(quarter === null ? 1 : (quarter - 1) * 3 + 1).padStart(2, "0")}-01`;
  const all = budgetLedger(r.kind === "line" ? r.budgetUsd : null, l.notes ?? [], l.pos, start, l.otherCost?.pos);
  const entries = reserveOnly ? all.filter((e) => e.kind === "reserve") : all;
  return (
    <section className="py-3 border-t border-line">
      <div className="overflow-x-auto">
        <table className="sheet text-[12px]">
          <thead>
            <tr>
              <th>Date</th>
              <th style={left} title="Sertica user no: who wrote the note, ordered the PO, or created the PO under approval">User</th>
              <th style={left}>Type</th>
              <th style={left}>Description</th>
              <th>Amount</th>
              {budgetUsd !== null && <th>Balance</th>}
            </tr>
          </thead>
          <tbody>
            {entries.map((e, i) => (
              <tr key={i}>
                <td style={{ whiteSpace: "nowrap" }}>{e.date ? formatDate(e.date) : "—"}</td>
                <td style={left}>{e.userNo ?? (e.kind === "base" ? "" : "—")}</td>
                <td style={adds(e) ? { ...left, ...ADDED } : takes(e) ? { ...left, ...TAKEN } : left}>{e.kind === "base" ? "BASE" : e.kind === "po" ? "PO" : e.kind === "reserve" ? "RESERVE" : TYPE[e.note.kind]}</td>
                <td style={{ ...left, whiteSpace: "normal", minWidth: 160 }}>
                  {e.kind === "base" ? (
                    `${periodLabel(l.period)} budget${l.sheet.forecast ? ` · forecast ${l.sheet.forecast.forecastNo}` : ""}`
                  ) : e.kind === "po" || e.kind === "reserve" ? (
                    <>
                      <button className="text-brand font-semibold hover:underline" onClick={() => onOpen({ type: "po", no: e.poNo })}>
                        {e.poNo}
                      </button>
                      {e.kind === "reserve" && <span className="text-muted"> · under approval</span>}
                    </>
                  ) : (
                    <>
                      {e.note.text.trim()} <span className="text-muted">· for {MONTH[e.note.month - 1]}</span>
                    </>
                  )}
                </td>
                <td className={e.amountUsd < 0 ? "neg" : ""} style={adds(e) ? ADDED : undefined}>{e.kind === "note" && e.note.amountUsd === null ? "—" : e.kind === "base" ? usd(e.amountUsd) : signed(e.amountUsd)}</td>
                {budgetUsd !== null && (
                  <td style={e.balanceUsd !== null && e.balanceUsd < 0 ? { background: "var(--budget-over)", color: "#fff", fontWeight: 600 } : undefined}>{usd(e.balanceUsd)}</td>
                )}
              </tr>
            ))}
            {entries.length === (budgetUsd === null ? 0 : 1) && (
              <tr>
                <td colSpan={budgetUsd === null ? 5 : 6} className="text-muted">
                  {reserveOnly ? "No POs under approval in this period." : "No transfers or POs in this period."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** One figure of the summary card: label over amount; a result is bold. */
export const Term = ({ label, children, result = false }: { label: string; children: ReactNode; result?: boolean }) => (
  <div className="min-w-0">
    <div className={`text-[11px] ${result ? "font-semibold text-ink2" : "text-muted"}`}>{label}</div>
    <div className={`tnum whitespace-nowrap ${result ? "text-[15px] font-extrabold" : "text-[13.5px] font-semibold"}`}>{children}</div>
  </div>
);

/** The operator between two figures, on the amounts' baseline. */
export const Op = ({ children }: { children: string }) => (
  <span aria-hidden className="self-end pb-0.5 text-[15px] font-bold text-muted">
    {children}
  </span>
);

/** One equation of the card, read left to right, wrapping on a narrow screen. */
export const Equation = ({ children }: { children: ReactNode }) => <div className="flex flex-wrap items-start gap-x-3 gap-y-2">{children}</div>;

/**
 * The line's figures as two equations, read left to right (SPEC §8.6): the base it started the
 * period with, plus what finance's forecast notes added and shifted in, less what they shifted out,
 * is the budget; the budget less spend and other cost is remaining. Deductions and other
 * adjustments show only when there are any, so the moves always add up to the budget. An unbudgeted
 * row has only spend and other cost.
 */
function Summary({ l }: { l: BudgetLine }) {
  const r = l.row;
  const spend = r.committedUsd + r.accruedUsd;
  const otherCost = l.otherCost ? usd(l.otherCost.totalUsd) : "—";
  if (r.kind !== "line")
    return (
      <section className="card mx-4 my-3 p-3">
        <Equation>
          <Term label="Actual">{usd(spend)}</Term>
          <Term label="Other cost">{otherCost}</Term>
        </Equation>
      </section>
    );
  const { budgetUsd, remainingUsd } = r;
  const m = budgetUsd !== null && l.notes !== null ? budgetMoves(budgetUsd, l.notes) : null;
  const budget = budgetUsd === null ? "not comparable" : budgetUsd === 0 ? "budget $0" : usd(budgetUsd);
  // A move: the operator carries its sign, the amount its size, green when it adds, red when it takes away.
  const moveTerm = (label: string, n: number) => (
    <Fragment key={label}>
      <Op>{n < -0.005 ? "−" : "+"}</Op>
      <Term label={label}>
        <span className={n < -0.005 ? "neg" : undefined} style={n > 0.005 ? ADDED : undefined}>
          {usd(Math.abs(n) < 0.005 ? 0 : Math.abs(n))}
        </span>
      </Term>
    </Fragment>
  );
  return (
    <section className="card mx-4 my-3 p-3 space-y-3">
      {m && (
        <Equation>
          <Term label="Base">{usd(m.baseUsd)}</Term>
          {moveTerm("Additional budget", m.additionalUsd)}
          {moveTerm("Shift in", m.shiftInUsd)}
          {/* Shown with its minus even when nothing shifted out. */}
          <Op>−</Op>
          <Term label="Shift out">
            <span className={m.shiftOutUsd < -0.005 ? "neg" : undefined}>{usd(Math.abs(m.shiftOutUsd) < 0.005 ? 0 : -m.shiftOutUsd)}</span>
          </Term>
          {Math.abs(m.deductionUsd) >= 0.005 && moveTerm("Deducted", m.deductionUsd)}
          {Math.abs(m.adjustmentUsd) >= 0.005 && moveTerm("Other adjustments", m.adjustmentUsd)}
          <Op>=</Op>
          <Term label="Budget" result>
            {budget}
          </Term>
        </Equation>
      )}
      <Equation>
        <Term label="Budget" result={!m}>
          {budget}
        </Term>
        <Op>−</Op>
        <Term label="Actual">{usd(spend)}</Term>
        <Op>−</Op>
        <Term label="Other cost">{otherCost}</Term>
        <Op>=</Op>
        <Term label="Remaining" result>
          <span className={remainingUsd !== null && remainingUsd < 0 ? "neg" : undefined}>{usd(remainingUsd)}</span>
        </Term>
      </Equation>
      <UsedBar budgetUsd={budgetUsd} spendUsd={spend} width={220} />
      {budgetUsd !== null && l.notes === null && (
        <p className="text-[11.5px] text-muted">No forecast in use, so no additional budget or shifts: they come from finance&apos;s notes on the line&apos;s forecast in Sertica.</p>
      )}
    </section>
  );
}

/**
 * Previous and Next through the list a drawer was opened from (SPEC §8.6), with where it is in it;
 * the ← and → keys do the same. Shown only when the drawer's row is on the list.
 */
export function Stepper<T>({ steps, at, onStep }: { steps: T[]; at: number; onStep: (s: T) => void }) {
  const prev = at > 0 ? steps[at - 1] : undefined;
  const next = at >= 0 && at < steps.length - 1 ? steps[at + 1] : undefined;
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey || (e.target as HTMLElement).closest("input, select, textarea")) return;
      const to = e.key === "ArrowLeft" ? prev : e.key === "ArrowRight" ? next : undefined;
      if (!to) return;
      e.preventDefault();
      onStep(to);
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [prev, next, onStep]);
  if (at < 0 || steps.length < 2) return null;
  const btn = "input text-[12.5px] px-2.5 disabled:opacity-40 disabled:cursor-default";
  return (
    <div className="px-4 pt-3 flex items-center gap-2">
      <button className={btn} disabled={!prev} onClick={() => prev && onStep(prev)} title="Previous (←)">
        ‹ Previous
      </button>
      <span className="text-[12px] text-muted tnum">
        {at + 1} of {steps.length}
      </span>
      <button className={btn} disabled={!next} onClick={() => next && onStep(next)} title="Next (→)">
        Next ›
      </button>
    </div>
  );
}

export function BudgetLineBody({
  row,
  period,
  otherCostOnly = false,
  onOpen,
  steps,
  onStep,
}: {
  row: BudgetRowRef;
  period: string;
  otherCostOnly?: boolean;
  onOpen: (r: DrawerRef) => void;
  steps?: BudgetLineStep[];
  onStep?: (s: BudgetLineStep) => void;
}) {
  const { filters, search } = useDashboard();
  const [reserveOnly, setReserveOnly] = useState(otherCostOnly);
  const params = { vessel: filters.vessel, period, forecasts: search.forecasts, unit: row.unitNo ?? undefined, row: row.row, pt: row.purchaseTypeNo ?? undefined, ta: row.technicalAccountNo ?? undefined };
  const { data: l, isLoading, isError, isPlaceholderData } = useQuery({
    queryKey: ["budgets/line", params],
    queryFn: () => api<BudgetLine>(`/budgets/line${qs(params)}`),
    // Stepping keeps the last line on screen, dimmed, until the next one loads.
    placeholderData: (prev) => prev,
  });
  const changes = useMemo(() => indexChanges(l?.changes ?? []).get(changeKey(l?.sheet.budgetNo ?? null, row.purchaseTypeNo, row.technicalAccountNo)), [l, row]);
  // Shown while the next line loads, so the buttons stay put when stepping quickly.
  const stepper = steps && onStep && <Stepper steps={steps} at={steps.findIndex((st) => st.period === period && sameRow(st.row, row))} onStep={onStep} />;
  if (isLoading)
    return (
      <>
        {stepper}
        <div className="p-4 text-muted">Loading…</div>
      </>
    );
  if (isError || !l)
    return (
      <>
        {stepper}
        <div className="p-4 text-muted">This row is not on the vessel's sheet for this period.</div>
      </>
    );

  const r = l.row;
  const isLine = r.kind === "line";
  const remainingUsd = isLine ? r.remainingUsd : null;
  const over = isLine && r.over;
  const vessel = l.sheet.unitName ?? l.sheet.unitNo ?? "No vessel";
  const key = row.row === "line" ? [row.purchaseTypeNo, row.technicalAccountNo] : [UNBUDGETED_LABEL[row.row], row.purchaseTypeNo, row.technicalAccountNo];
  return (
    <div className={isPlaceholderData ? "opacity-60" : undefined}>
      {stepper}
      <div className="px-4 pt-3 pb-2 space-y-2">
        <div>
          <div className="label">{isLine ? "Budget line" : "Unbudgeted spend"} · {periodLabel(l.period)}</div>
          <h2 className="text-[18px] font-extrabold leading-snug">
            {vessel} <span className="text-muted font-semibold">· {key.filter(Boolean).join(" · ")}</span>
          </h2>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {over && (
            <>
              <OverChip />
              <span className="text-[12.5px] neg tnum">over by {usd(-(remainingUsd ?? 0))}</span>
            </>
          )}
          <ChangeMark changes={changes} />
          {!isLine && <span className="text-[12px] text-muted">Matches no budget line, so it is never flagged.</span>}
        </div>
      </div>
      <Summary l={l} />
      {otherCostOnly && (
        <div className="px-4 pt-3 border-t border-line flex items-center gap-2 text-[12.5px]">
          <span className="font-semibold">{reserveOnly ? "POs under approval (other cost)" : "Full ledger"}</span>
          <button className="text-brand hover:underline" onClick={() => setReserveOnly((v) => !v)}>
            {reserveOnly ? "Show full ledger" : "Show only POs under approval"}
          </button>
        </div>
      )}
      <Ledger l={l} onOpen={onOpen} reserveOnly={reserveOnly} />
    </div>
  );
}
